import { useEffect, useRef, useState } from 'react';

interface ICacheEntry {
    thumbnails: string[];
    generatedWidth: number;
}

// Module-level cache so reopening the timeline for the same video never regenerates.
// Object URLs are revoked only on eviction, never on component unmount.
const thumbnailCache = new Map<string, ICacheEntry>();
const MAX_CACHE_ENTRIES = 6;

const TARGET_THUMB_WIDTH = 80; // ~one thumbnail per 80px of timeline
const THUMB_RENDER_WIDTH = 160; // capture resolution (px), 2x display width for sharpness
const SEEK_TIMEOUT_MS = 5000;

const getThumbnailCount = (timelineWidth: number) =>
    Math.min(40, Math.max(2, Math.ceil(timelineWidth / TARGET_THUMB_WIDTH)));

const evictOldestEntries = () => {
    while (thumbnailCache.size > MAX_CACHE_ENTRIES) {
        const oldestKey = thumbnailCache.keys().next().value;
        if (oldestKey === undefined) return;
        const entry = thumbnailCache.get(oldestKey);
        thumbnailCache.delete(oldestKey);
        entry?.thumbnails.forEach((url) => URL.revokeObjectURL(url));
    }
};

const waitForSeek = (video: HTMLVideoElement) =>
    new Promise<void>((resolve, reject) => {
        let timer: ReturnType<typeof setTimeout>;
        const cleanup = () => {
            clearTimeout(timer);
            video.removeEventListener('seeked', onSeeked);
            video.removeEventListener('error', onError);
        };
        const onSeeked = () => {
            cleanup();
            resolve();
        };
        const onError = () => {
            cleanup();
            reject(new Error('Video failed to load for thumbnail generation'));
        };
        // Some browsers skip the seeked event for no-op seeks; fall back to
        // drawing whatever frame is available rather than hanging forever.
        timer = setTimeout(onSeeked, SEEK_TIMEOUT_MS);
        video.addEventListener('seeked', onSeeked);
        video.addEventListener('error', onError);
    });

const waitForMetadata = (video: HTMLVideoElement) =>
    new Promise<void>((resolve, reject) => {
        if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
            resolve();
            return;
        }
        const cleanup = () => {
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
        };
        const onLoaded = () => {
            cleanup();
            resolve();
        };
        const onError = () => {
            cleanup();
            reject(new Error('Video failed to load for thumbnail generation'));
        };
        video.addEventListener('loadedmetadata', onLoaded);
        video.addEventListener('error', onError);
    });

export interface IVideoThumbnails {
    /** Object URLs of captured frames, in timeline order. Grows as frames stream in. */
    thumbnails: string[];
    /** Total slots the timeline should render (0 while width/metadata are unknown). */
    totalCount: number;
}

/**
 * Extracts evenly-spaced frames from a video for use as a timeline strip.
 * Frames stream in progressively; completed sets are cached globally by URL.
 * On CORS/decode failure resolves to an empty set — callers render a plain strip.
 */
export const useVideoThumbnails = (
    videoUrl: string | undefined,
    duration: number,
    timelineWidth: number
): IVideoThumbnails => {
    const [result, setResult] = useState<IVideoThumbnails>({ thumbnails: [], totalCount: 0 });

    // Width is read once when generation starts ("generate once, stretch on resize").
    // generatedWidth is kept in the cache so threshold-based regeneration can be added later.
    const widthRef = useRef(timelineWidth);
    widthRef.current = timelineWidth;
    const hasWidth = timelineWidth > 0;

    useEffect(() => {
        if (!videoUrl || !hasWidth || !Number.isFinite(duration) || duration <= 0) return undefined;

        const cached = thumbnailCache.get(videoUrl);
        if (cached) {
            setResult({ thumbnails: cached.thumbnails, totalCount: cached.thumbnails.length });
            return undefined;
        }

        let cancelled = false;
        const produced: string[] = [];
        const count = getThumbnailCount(widthRef.current);
        setResult({ thumbnails: [], totalCount: count });

        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.playsInline = true;
        // 'auto' so the browser buffers ahead of the sequential seeks — with
        // 'metadata' every seek is a cold range-fetch that can outrun the seek
        // timeout and capture blank frames, which would then be cached.
        video.preload = 'auto';
        video.src = videoUrl;

        const generate = async () => {
            await waitForMetadata(video);
            if (cancelled) return;

            const sourceDuration = Number.isFinite(video.duration) ? video.duration : duration;
            const aspect =
                video.videoWidth > 0 && video.videoHeight > 0
                    ? video.videoHeight / video.videoWidth
                    : 9 / 16;
            const canvas = document.createElement('canvas');
            canvas.width = THUMB_RENDER_WIDTH;
            canvas.height = Math.round(THUMB_RENDER_WIDTH * aspect);
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            // Frames are captured sequentially on purpose: a single element can only
            // decode one seek at a time, and this is what lets thumbnails stream in.
            for (let i = 0; i < count; i++) {
                if (cancelled) return;
                const time = (i / (count - 1)) * sourceDuration;
                // Exact 0 and exact duration seek unreliably across browsers.
                video.currentTime = Math.min(
                    Math.max(time, 0.01),
                    Math.max(0.01, sourceDuration - 0.1)
                );
                // eslint-disable-next-line no-await-in-loop
                await waitForSeek(video);
                if (cancelled) return;

                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                // Throws SecurityError here if the canvas is tainted (CORS failure).
                // eslint-disable-next-line no-await-in-loop
                const blob = await new Promise<Blob | null>((resolve) => {
                    canvas.toBlob(resolve, 'image/jpeg', 0.7);
                });
                if (cancelled) return;
                if (blob) {
                    produced.push(URL.createObjectURL(blob));
                    setResult({ thumbnails: [...produced], totalCount: count });
                }
            }

            thumbnailCache.set(videoUrl, { thumbnails: produced, generatedWidth: widthRef.current });
            evictOldestEntries();
        };

        generate().catch(() => {
            // CORS taint, decode error, or missing frames — degrade to a plain strip.
            produced.forEach((url) => URL.revokeObjectURL(url));
            produced.length = 0;
            if (!cancelled) setResult({ thumbnails: [], totalCount: 0 });
        });

        return () => {
            cancelled = true;
            // Only completed sets enter the cache; discard partials.
            if (!thumbnailCache.has(videoUrl)) {
                produced.forEach((url) => URL.revokeObjectURL(url));
            }
            video.removeAttribute('src');
            video.load();
        };
    }, [videoUrl, duration, hasWidth]);

    return result;
};
